import { createContext, useContext, useEffect } from "react";

/**
 * 受講生が出す共有のリンクを、紹介コードつき (紹介リンク) にする。
 *
 * 共有の住所は yumesaku.tech の今の頁の住所のまま、ref=<紹介コード> を足すだけ。既にある引数と # は崩さない。
 * 報酬のある紹介なので、投稿の文に「紹介リンク」と分かる語を入れる (2026-10-23 代表の決め)。
 * 受け取る側は apps/client/app/lib/referral/referral-landing.ts (cookie と LINE の入口と決済)。
 * 紹介コードを持つかどうか (ログイン済みで体験でない受講生だけ) は、アプリの側が ShareReferralContext で渡す
 */

/** 投稿の文に入れる語 */
export const REFERRAL_LINK_LABEL = "紹介リンク";

/** 紹介リンクを写せたときの知らせ。写した住所には語が付かないので、添えてもらう */
export const REFERRAL_LINK_COPIED_MESSAGE = "紹介リンクをコピーしました。投稿には「紹介リンク」と添えてください";

const REFERRAL_CODE_PATTERN = /^[A-Za-z0-9]{4,10}$/;
const REFERRAL_HOST = "yumesaku.tech";

/**
 * yumesaku.tech の住所に ref=<code> を足す。ほかのホスト、相対の住所、既に ref がある住所、
 * 形の合わないコードでは、受け取った文字列をそのまま返す
 */
export function withReferral(url: string, code: string | null | undefined): string {
  if (!code || !REFERRAL_CODE_PATTERN.test(code)) return url;
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return url;
  }
  if (parsed.hostname !== REFERRAL_HOST) return url;
  if (parsed.searchParams.has("ref")) return url;
  // URL の組み直し (toString) は既にある引数の書き方を変えるので、文字列のまま差し込む
  const hashAt = url.indexOf("#");
  const beforeHash = hashAt === -1 ? url : url.slice(0, hashAt);
  const hash = hashAt === -1 ? "" : url.slice(hashAt);
  const joiner = !beforeHash.includes("?") ? "?" : /[?&]$/.test(beforeHash) ? "" : "&";
  return `${beforeHash}${joiner}ref=${code}${hash}`;
}

/**
 * 文の中の yumesaku.tech の住所。ホストの直後が / ? # か住所の終わりのときだけ当て (yumesaku.tech.example.com に当てない)、
 * 住所の終わりは空白か 。、）」 で切る (句読点の後ろに ref を足して 404 にしない)
 */
const YUMESAKU_URL_IN_TEXT = /https:\/\/yumesaku\.tech(?=[/?#\s。、）」]|$)(?:[/?#][^\s。、）」]*)?/g;

/** 文の中の yumesaku.tech の住所に ref を足し、最初の住所の前に「紹介リンク」を置く。足せる住所が無ければそのまま */
export function withReferralInText(text: string, code: string | null | undefined): string {
  let first = true;
  let changed = false;
  const next = text.replace(YUMESAKU_URL_IN_TEXT, (match) => {
    // 文の終わりの半角の . , ! ? ) は住所に含めない
    const tail = /[.,!?)]+$/.exec(match)?.[0] ?? "";
    const found = match.slice(0, match.length - tail.length);
    const replaced = withReferral(found, code);
    if (replaced === found) return match;
    changed = true;
    const out = first ? `${REFERRAL_LINK_LABEL} ${replaced}` : replaced;
    first = false;
    return `${out}${tail}`;
  });
  return changed ? next : text;
}

/** 共有の窓の住所の url の引数 (共有する頁の住所)。無ければ null */
export function sharedUrlOf(href: string): string | null {
  try {
    return new URL(href).searchParams.get("url");
  } catch {
    return null;
  }
}

/**
 * note に写すひな形を、紹介リンクに合わせる。ひな形の中の住所に ref と語を足し、
 * ひな形が住所を持たない (共有の素材の頁のように本文だけの) ときは、終わりに「紹介リンク <住所>」の 1 行を足す。
 * 窓の住所が紹介リンクでなければ (referralHref が元のまま) ひな形も変えない
 */
export function referralNoteTemplate(template: string, href: string, referralHref: string, code: string | null | undefined): string {
  if (referralHref === href) return template;
  const inText = withReferralInText(template, code);
  if (inText !== template) return inText;
  const url = sharedUrlOf(referralHref);
  if (!url) return template;
  return `${template.replace(/\s+$/, "")}\n\n${REFERRAL_LINK_LABEL} ${url}`;
}

/**
 * 共有の窓 (X と note) の住所を、紹介リンクにして返す。
 * - X (x.com と twitter.com の intent) は、url の引数に ref を足し、文の終わりに「紹介リンク」を置く (X は文、住所、タグの順に並べる)。
 *   url の引数が無く文の中に住所がある形は、文の中の住所に足す
 * - note (note.com の intent) は url の引数に足す。note は本文を受け取らないので、語はひな形の側 (withReferralInText) に入れる
 * どれにも当たらない住所や、足せない住所はそのまま返す
 */
export function referralShareHref(href: string, code: string | null | undefined): string {
  if (!code || !REFERRAL_CODE_PATTERN.test(code)) return href;
  let intent: URL;
  try {
    intent = new URL(href);
  } catch {
    return href;
  }
  const host = intent.hostname.replace(/^www\./, "");
  const isX = (host === "x.com" || host === "twitter.com") && /^\/intent\/(post|tweet)\/?$/.test(intent.pathname);
  const isNote = host === "note.com" && /^\/intent\/post\/?$/.test(intent.pathname);
  if (!isX && !isNote) return href;
  const url = intent.searchParams.get("url");
  if (url) {
    const next = withReferral(url, code);
    if (next === url) return href;
    intent.searchParams.set("url", next);
    if (isX) {
      const text = intent.searchParams.get("text") ?? "";
      intent.searchParams.set("text", text ? `${text}\n${REFERRAL_LINK_LABEL}` : REFERRAL_LINK_LABEL);
    }
    return intent.toString();
  }
  if (isX) {
    const text = intent.searchParams.get("text");
    if (!text) return href;
    const next = withReferralInText(text, code);
    if (next === text) return href;
    intent.searchParams.set("text", next);
    return intent.toString();
  }
  return href;
}

export interface ShareReferral {
  /** 今の人の紹介コード。持たない人 (未ログイン、体験の人) と、まだ取れていないときは null */
  code: string | null;
  /** 共有の釦が出たときに呼ぶ。アプリの側が初めの 1 回だけ紹介コードを取りに行く */
  request(): void;
}

/** アプリの側が渡す。渡さない所 (書き出しの頁など) では共有の住所は変わらない */
export const ShareReferralContext = createContext<ShareReferral | null>(null);

/**
 * 共有の釦の中で使う。出たときに紹介コードを頼み、取れていれば返す。
 * 釦を出さない画面 (enabled が false) では頼まない (初めての人には紹介コードを作るので、要らない所で走らせない)
 */
export function useShareReferralCode(enabled: boolean = true): string | null {
  const referral = useContext(ShareReferralContext);
  useEffect(() => {
    if (enabled) referral?.request();
  }, [referral, enabled]);
  return enabled ? (referral?.code ?? null) : null;
}
